import React from 'react';
import Tag from '@ui/tag/Tag';
import Switcher from '@ui/switcher/Switcher';
import RunHistoryStrip, { Run } from '@ui/runhistory/RunHistoryStrip';
import { Integration } from '@app/model/integration-settings/Integration';
import { backfillChipText, formatDayTime, RESULT_TAG, STATUS_TAG, TYPE_LABEL } from '../IntegrationSettings.utilities';

export const runsOf = (integration: Integration): Run[] => integration.recentRuns.map(run => ({
	tone: run.tone,
	title: run.startedAt === null ? RESULT_TAG[run.result].label : `${formatDayTime(run.startedAt)} · ${RESULT_TAG[run.result].label}`,
}));

export const IntegrationRunHistory: React.FunctionComponent<{ integration: Integration }> = ({ integration }) => {
	const runs = runsOf(integration);
	if (runs.length > 0 && integration.recentRuns.every(run => run.result === 'WAITING')) {
		return <Tag state="none" title={runs[runs.length - 1].title}>Waiting</Tag>;
	}
	return <RunHistoryStrip label={`Last runs of the ${TYPE_LABEL[integration.type]} integration`} runs={runs} />;
};

export const StatusTag: React.FunctionComponent<{ integration: Integration }> = ({ integration }) =>
	<Tag state={STATUS_TAG[integration.status].state}>{STATUS_TAG[integration.status].label}</Tag>;

// Shown only while the backfill is not complete (the overview and the cards agree).
export const BackfillTag: React.FunctionComponent<{ integration: Integration }> = ({ integration }) => {
	const text = integration.backfill === null ? null : backfillChipText(integration.backfill);
	return text === null ? null : <Tag state="info">{text}</Tag>;
};

// The right side of a card header: the run history, the status, the backfill progress and the switch.
const IntegrationHeaderActions: React.FunctionComponent<{
	integration: Integration,
	onToggle: (enabled: boolean) => void,
	isSaving: boolean,
}> = ({ integration, onToggle, isSaving }) => <>
	<IntegrationRunHistory integration={integration} />
	<StatusTag integration={integration} />
	<BackfillTag integration={integration} />
	<Switcher isLarge checked={integration.enabled} disabled={integration.status === 'NOT_SET_UP' || isSaving || (!integration.enabled && (!integration.catalogue.ready || integration.catalogue.busy))}
						ariaLabel={`${TYPE_LABEL[integration.type]} integration on`} onChange={onToggle} />
</>;

export default IntegrationHeaderActions;
